import React, { useState, useEffect } from 'react';
import { 
  Search, 
  User, 
  Phone, 
  Car, 
  Plus, 
  ChevronDown,
  Mail,
  MapPin,
  Calendar,
  Fuel,
  Settings,
  Hash,
  CheckCircle,
  XCircle,
  ArrowLeft
} from 'lucide-react';
import { getPopularCountries, getCountryByCode, searchCountries, Country, COUNTRIES } from '../data/countryCodes';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../api/config';
import toast from 'react-hot-toast';
import { handleApiError } from '../utils/errorHandler';
import { useNavigate } from 'react-router-dom';

interface Customer {
  id: number;
  name: string;
  country_code: string;
  phone_number_only: string;
  email?: string;
  address?: string;
  language: string;
  is_active: boolean;
  created_at: string;
}

interface Vehicle {
  id: number;
  customer_id: number;
  make: string;
  model: string;
  year: number;
  color: string;
  plate_number: string;
  vin?: string;
  engine_number?: string;
  mileage: number;
  fuel_type: string;
  transmission_type: string;
  notes?: string;
}

interface CustomerSearchResult {
  found: boolean;
  customer?: Customer;
  vehicles?: Vehicle[];
}

const CustomerCheckIn: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  // Phone search states
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResult, setSearchResult] = useState<CustomerSearchResult | null>(null);
  
  // Customer form states
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const [customerFormData, setCustomerFormData] = useState({
    name: '',
    email: '',
    address: '',
    language: language || 'en'
  });
  const [customerFormLoading, setCustomerFormLoading] = useState(false);
  
  // Vehicle form states
  const [showVehicleForm, setShowVehicleForm] = useState(false);
  const [vehicleFormData, setVehicleFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    color: '',
    plate_number: '',
    vin: '',
    engine_number: '',
    mileage: 0,
    fuel_type: 'Petrol',
    transmission_type: 'Automatic',
    notes: ''
  });
  const [vehicleFormLoading, setVehicleFormLoading] = useState(false);
  
  // Selected customer state
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerVehicles, setCustomerVehicles] = useState<Vehicle[]>([]);

  // Get filtered countries (excluding Israel)
  const getFilteredCountries = () => {
    return COUNTRIES.filter(country => country.code !== 'IL');
  };

  const popularCountries = getPopularCountries().filter(country => country.code !== 'IL');
  const allCountries = searchCountries(countrySearchTerm).filter(country => country.code !== 'IL');
  const displayCountries = countrySearchTerm ? allCountries : popularCountries;

  useEffect(() => {
    // Default to Kuwait
    const kuwait = getCountryByCode('KW');
    if (kuwait) {
      setSelectedCountry(kuwait);
    }
  }, []);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    setCountrySearchTerm('');
  };

  const handleSearch = async () => {
    if (!selectedCountry || !phoneNumber.trim()) {
      toast.error(t('pleaseEnterPhoneNumber'));
      return;
    }

    setSearchLoading(true);
    try {
      const response = await apiClient.post('/customers/search-by-phone', {
        country_code: selectedCountry.dialCode,
        phone_number: phoneNumber.trim()
      });

      const result: CustomerSearchResult = response.data;
      setSearchResult(result);

      if (result.found && result.customer) {
        setSelectedCustomer(result.customer);
        setCustomerVehicles(result.vehicles || []);
        setShowCustomerForm(false);
        setShowVehicleForm(false);
      }
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleCreateCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCountry || !phoneNumber.trim()) {
      toast.error(t('pleaseEnterPhoneNumber'));
      return;
    }

    setCustomerFormLoading(true);
    try {
      const response = await apiClient.post('/customers', {
        name: customerFormData.name,
        email: customerFormData.email,
        phone_number: `+${selectedCountry.dialCode}${phoneNumber.trim()}`,
        country_code: selectedCountry.dialCode,
        phone_number_only: phoneNumber.trim(),
        address: customerFormData.address,
        language: customerFormData.language,
        role: 'customer'
      });

      const newCustomer = response.data.data;
      setSelectedCustomer(newCustomer);
      setCustomerVehicles([]);
      setShowCustomerForm(false);
      toast.success(t('customerCreatedSuccessfully'));
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    } finally {
      setCustomerFormLoading(false);
    }
  };

  const handleCreateVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedCustomer) {
      toast.error(t('pleaseSelectCustomerFirst'));
      return;
    }

    setVehicleFormLoading(true);
    try {
      const response = await apiClient.post('/vehicles', {
        ...vehicleFormData,
        customer_id: selectedCustomer.id
      });

      const newVehicle = response.data.data;
      setCustomerVehicles([...customerVehicles, newVehicle]);
      setShowVehicleForm(false);
      
      // Reset vehicle form
      setVehicleFormData({
        make: '',
        model: '',
        year: new Date().getFullYear(),
        color: '',
        plate_number: '',
        vin: '',
        engine_number: '',
        mileage: 0,
        fuel_type: 'Petrol',
        transmission_type: 'Automatic',
        notes: ''
      });
      
      toast.success(t('vehicleAddedSuccessfully'));
    } catch (error) {
      const processedError = handleApiError(error);
      toast.error(processedError.message);
    } finally {
      setVehicleFormLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !showCustomerForm && !showVehicleForm) {
      handleSearch();
    }
  };

  const handleCheckIn = (vehicle: Vehicle) => {
    // Navigate to job card creation with customer and vehicle data
    navigate('/job-cards/new', {
      state: {
        customerId: selectedCustomer?.id,
        vehicleId: vehicle.id,
        customerName: selectedCustomer?.name,
        vehicleInfo: `${vehicle.year} ${vehicle.make} ${vehicle.model}`
      }
    });
  };

  const resetSearch = () => {
    setSearchResult(null);
    setSelectedCustomer(null);
    setCustomerVehicles([]);
    setPhoneNumber('');
    setShowCustomerForm(false);
    setShowVehicleForm(false);
  };

  return (
    <div className={`min-h-screen bg-gray-50 p-4 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-800 mb-4"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            {t('back')}
          </button>
          
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <User className="h-8 w-8 mr-3 text-teal-600" />
            {t('customerCheckIn')}
          </h1>
          <p className="text-gray-600 mt-2">{t('searchOrRegisterCustomer')}</p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Search/Register */}
          <div>
            {/* Phone Search Card */}
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <div className="flex items-center mb-4">
                <Search className="h-6 w-6 text-teal-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">
                  {t('customerLookup')}
                </h2>
              </div>

              {/* Phone Number Input */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('phoneNumber')}
                  </label>
                  <div className="flex">
                    {/* Country Code Dropdown */}
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                        className="flex items-center px-3 py-2 border border-gray-300 rounded-l-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[120px]"
                      >
                        {selectedCountry ? (
                          <div className="flex items-center">
                            <span className="mr-1">{selectedCountry.flag}</span>
                            <span className="text-sm">+{selectedCountry.dialCode}</span>
                            <ChevronDown className="h-4 w-4 ml-1" />
                          </div>
                        ) : (
                          <span className="text-gray-500">{t('selectCountry')}</span>
                        )}
                      </button>

                      {showCountryDropdown && (
                        <div className="absolute top-full left-0 mt-1 w-80 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-64 overflow-hidden">
                          {/* Search within dropdown */}
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              placeholder={t('searchCountries')}
                              value={countrySearchTerm}
                              onChange={(e) => setCountrySearchTerm(e.target.value)}
                              className="w-full px-2 py-1 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                            />
                          </div>
                          
                          <div className="overflow-y-auto max-h-48">
                            {displayCountries.map((country) => (
                              <button
                                key={country.code}
                                onClick={() => handleCountrySelect(country)}
                                className="w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between"
                              >
                                <div className="flex items-center">
                                  <span className="mr-2">{country.flag}</span>
                                  <span className="text-sm">
                                    {language === 'ar' ? country.nameAr : country.name}
                                  </span>
                                </div>
                                <span className="text-sm text-gray-500">+{country.dialCode}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Phone Number Input */}
                    <input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder={t('enterPhoneNumber')}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      dir="ltr"
                    />
                  </div>
                </div>

                {/* Search/Reset Buttons */}
                <div className="flex space-x-2">
                  <button
                    onClick={handleSearch}
                    disabled={searchLoading || !selectedCountry || !phoneNumber.trim()}
                    className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {searchLoading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Search className="h-5 w-5 mr-2" />
                    )}
                    {searchLoading ? t('searching') : t('search')}
                  </button>
                  
                  {(searchResult || selectedCustomer) && (
                    <button
                      onClick={resetSearch}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {t('newSearch')}
                    </button>
                  )}
                </div>
              </div>

              {/* Search Results */}
              {searchResult && !selectedCustomer && (
                <div className="mt-6 p-4 border rounded-lg">
                  {searchResult.found ? (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-5 w-5 mr-2" />
                      <span className="font-medium">{t('customerFound')}</span>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center text-orange-600">
                        <XCircle className="h-5 w-5 mr-2" />
                        <span className="font-medium">{t('customerNotFound')}</span>
                      </div>
                      
                      <button
                        onClick={() => setShowCustomerForm(true)}
                        className="w-full bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center justify-center"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        {t('registerNewCustomer')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Customer Registration Form */}
            {showCustomerForm && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-teal-600" />
                  {t('newCustomerRegistration')}
                </h3>
                
                <form onSubmit={handleCreateCustomer} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('fullName')} *
                    </label>
                    <input
                      type="text"
                      required
                      value={customerFormData.name}
                      onChange={(e) => setCustomerFormData({...customerFormData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder={t('enterFullName')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('email')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="email"
                        value={customerFormData.email}
                        onChange={(e) => setCustomerFormData({...customerFormData, email: e.target.value})}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder={t('enterEmail')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('address')}
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <textarea
                        value={customerFormData.address}
                        onChange={(e) => setCustomerFormData({...customerFormData, address: e.target.value})}
                        className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder={t('enterAddress')}
                        rows={3}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('preferredLanguage')}
                    </label>
                    <select
                      value={customerFormData.language}
                      onChange={(e) => setCustomerFormData({...customerFormData, language: e.target.value as any})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    >
                      <option value="en">{t('english')}</option>
                      <option value="ar">{t('arabic')}</option>
                    </select>
                  </div>

                  <div className="flex space-x-2 pt-4">
                    <button
                      type="submit"
                      disabled={customerFormLoading || !customerFormData.name}
                      className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {customerFormLoading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                      ) : (
                        t('registerCustomer')
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCustomerForm(false)}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>

          {/* Right Column - Customer Details & Vehicles */}
          <div>
            {selectedCustomer && (
              <>
                {/* Customer Details Card */}
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <User className="h-5 w-5 mr-2 text-teal-600" />
                    {t('customerDetails')}
                  </h3>
                  
                  <div className="space-y-3">
                    <div>
                      <span className="text-sm text-gray-600">{t('name')}:</span>
                      <p className="font-medium">{selectedCustomer.name}</p>
                    </div>
                    
                    <div>
                      <span className="text-sm text-gray-600">{t('phone')}:</span>
                      <p className="font-medium" dir="ltr">
                        +{selectedCustomer.country_code} {selectedCustomer.phone_number_only}
                      </p>
                    </div>
                    
                    {selectedCustomer.email && (
                      <div>
                        <span className="text-sm text-gray-600">{t('email')}:</span>
                        <p className="font-medium">{selectedCustomer.email}</p>
                      </div>
                    )}
                    
                    {selectedCustomer.address && (
                      <div>
                        <span className="text-sm text-gray-600">{t('address')}:</span>
                        <p className="font-medium">{selectedCustomer.address}</p>
                      </div>
                    )}
                    
                    <div>
                      <span className="text-sm text-gray-600">{t('customerSince')}:</span>
                      <p className="font-medium">
                        {new Date(selectedCustomer.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Vehicles Card */}
                <div className="bg-white rounded-lg shadow-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center">
                      <Car className="h-5 w-5 mr-2 text-teal-600" />
                      {t('vehicles')} ({customerVehicles.length})
                    </h3>
                    
                    <button
                      onClick={() => setShowVehicleForm(!showVehicleForm)}
                      className="bg-teal-600 text-white px-3 py-1 rounded-md hover:bg-teal-700 flex items-center text-sm"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      {t('addVehicle')}
                    </button>
                  </div>

                  {/* Vehicle Form */}
                  {showVehicleForm && (
                    <form onSubmit={handleCreateVehicle} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('make')} *
                          </label>
                          <input
                            type="text"
                            required
                            value={vehicleFormData.make}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, make: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder="Toyota"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('model')} *
                          </label>
                          <input
                            type="text"
                            required
                            value={vehicleFormData.model}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, model: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder="Camry"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('year')} *
                          </label>
                          <input
                            type="number"
                            required
                            min="1900"
                            max={new Date().getFullYear() + 1}
                            value={vehicleFormData.year}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, year: parseInt(e.target.value)})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('color')} *
                          </label>
                          <input
                            type="text"
                            required
                            value={vehicleFormData.color}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, color: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder={t('silver')}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('plateNumber')} *
                          </label>
                          <input
                            type="text"
                            required
                            value={vehicleFormData.plate_number}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, plate_number: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                            placeholder="ABC 123"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('mileage')} (km) *
                          </label>
                          <input
                            type="number"
                            required
                            min="0"
                            value={vehicleFormData.mileage}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, mileage: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('fuelType')}
                          </label>
                          <select
                            value={vehicleFormData.fuel_type}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, fuel_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                            <option value="Petrol">{t('petrol')}</option>
                            <option value="Diesel">{t('diesel')}</option>
                            <option value="Hybrid">{t('hybrid')}</option>
                            <option value="Electric">{t('electric')}</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('transmission')}
                          </label>
                          <select
                            value={vehicleFormData.transmission_type}
                            onChange={(e) => setVehicleFormData({...vehicleFormData, transmission_type: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          >
                            <option value="Automatic">{t('automatic')}</option>
                            <option value="Manual">{t('manual')}</option>
                            <option value="CVT">CVT</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          VIN
                        </label>
                        <input
                          type="text"
                          value={vehicleFormData.vin}
                          onChange={(e) => setVehicleFormData({...vehicleFormData, vin: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder="17-character VIN"
                          maxLength={17}
                        />
                      </div>

                      <div className="flex space-x-2 pt-2">
                        <button
                          type="submit"
                          disabled={vehicleFormLoading}
                          className="flex-1 bg-teal-600 text-white px-4 py-2 rounded-md hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {vehicleFormLoading ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mx-auto"></div>
                          ) : (
                            t('addVehicle')
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowVehicleForm(false)}
                          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          {t('cancel')}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Vehicles List */}
                  {customerVehicles.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Car className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                      <p>{t('noVehiclesRegistered')}</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {customerVehicles.map((vehicle) => (
                        <div key={vehicle.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </h4>
                              
                              <div className="grid grid-cols-2 gap-2 mt-2 text-sm text-gray-600">
                                <div className="flex items-center">
                                  <Hash className="h-3 w-3 mr-1" />
                                  {t('plate')}: {vehicle.plate_number}
                                </div>
                                <div className="flex items-center">
                                  <div className="w-3 h-3 rounded-full mr-1" style={{backgroundColor: vehicle.color.toLowerCase()}}></div>
                                  {t('color')}: {vehicle.color}
                                </div>
                                <div className="flex items-center">
                                  <Fuel className="h-3 w-3 mr-1" />
                                  {vehicle.fuel_type}
                                </div>
                                <div className="flex items-center">
                                  <Settings className="h-3 w-3 mr-1" />
                                  {vehicle.transmission_type}
                                </div>
                              </div>
                              
                              <div className="mt-2 text-sm text-gray-500">
                                {t('mileage')}: {vehicle.mileage.toLocaleString()} km
                              </div>
                              
                              {vehicle.vin && (
                                <div className="mt-1 text-sm text-gray-500">
                                  VIN: {vehicle.vin}
                                </div>
                              )}
                            </div>
                            
                            <button
                              onClick={() => handleCheckIn(vehicle)}
                              className="ml-4 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center text-sm"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              {t('checkIn')}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerCheckIn;